import { Endpoints } from "@/shared/API/APIConfig";
import { baseApi, type ApiResponse } from "./base.api";
import type {
  GetProduceOutputsParams,
  ProduceOutputHelpDataResponse,
  ProduceOutputListApiResponse,
} from "@/features/produced-output/types/producedOutput.types";
import type {
  CreateProduceOutputRequest,
  ProduceOutputPrepDetails,
  ShowPendingRecordApiResponse,
  ShowPreparedItemApiResponse,
} from "@/features/produced-output/types/produceOutputFlow.types";
import { mapApiProduceOutputToItem } from "@/features/produced-output/utils/producedOutput.utils";
import { mapShowPreparedItemToPrepDetails } from "@/features/produced-output/utils/produceOutputFlow.utils";
import type { ProducedOutputItem } from "@/features/produced-output/types/producedOutput.types";
import type { ProduceOutputApiResponse } from "@/features/produced-output/types/batchDetails.types";

export interface ProduceOutputsQueryResult {
  items: ProducedOutputItem[];
  awaitingCount: number;
  total: number;
  totalPages: number;
  currentPage: number;
  perPage: number;
}

export const producedOutputApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProduceOutputs: builder.query<
      ProduceOutputsQueryResult,
      GetProduceOutputsParams
    >({
      query: ({
        page = 1,
        limit = 10,
        search,
        category_ids,
        subcategory_ids,
        production_date_from,
        production_date_to,
        is_no_variance,
        sort_by,
        sort_dir,
      }) => {
        const queryParams = new URLSearchParams();
        queryParams.set("per_page", String(limit));
        queryParams.set("page", String(page));

        if (search) {
          queryParams.set("search", search);
        }

        const addArrayParams = (key: string, values?: number[]) => {
          if (values && values.length > 0) {
            values.forEach((value, index) => {
              queryParams.append(`${key}[${index}]`, String(value));
            });
          }
        };

        addArrayParams("category_ids", category_ids);
        addArrayParams("subcategory_ids", subcategory_ids);

        if (production_date_from) {
          queryParams.set("production_date_from", production_date_from);
        }
        if (production_date_to) {
          queryParams.set("production_date_to", production_date_to);
        }
        if (is_no_variance !== undefined) {
          queryParams.set("is_no_variance", String(is_no_variance));
        }
        if (sort_by) {
          queryParams.set("sort_by", sort_by);
        }
        if (sort_dir) {
          queryParams.set("sort_dir", sort_dir);
        }

        return `${Endpoints.produceOutput.getAll}?${queryParams.toString()}`;
      },
      transformResponse: (
        response: ApiResponse<ProduceOutputListApiResponse>,
      ): ProduceOutputsQueryResult => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load produce outputs");
        }

        const { awaiting_output, produce_outputs } = response.data;

        return {
          items: produce_outputs.data.map(mapApiProduceOutputToItem),
          awaitingCount: awaiting_output,
          total: produce_outputs.total,
          totalPages: produce_outputs.total_pages,
          currentPage: produce_outputs.current_page,
          perPage: produce_outputs.per_page,
        };
      },
      providesTags: [{ type: "ProduceOutputs", id: "LIST" }],
    }),
    getProduceOutputHelperData: builder.query<ProduceOutputHelpDataResponse, void>({
      query: () => Endpoints.produceOutput.getHelperData,
      transformResponse: (
        response: ApiResponse<ProduceOutputHelpDataResponse>,
      ): ProduceOutputHelpDataResponse => {
        if (!response.success) {
          throw new Error(
            response.message || "Failed to load produce output helper data",
          );
        }
        return response.data;
      },
      providesTags: [{ type: "ProduceOutputs", id: "HelperData" }],
    }),
    getShowPreparedItem: builder.query<
      ProduceOutputPrepDetails,
      string | number
    >({
      query: (id) => Endpoints.produceOutput.showPreparedItem(id),
      transformResponse: (
        response: ApiResponse<ShowPreparedItemApiResponse>,
      ): ProduceOutputPrepDetails => {
        if (!response.success) {
          throw new Error(
            response.message || "Failed to load prepared item details",
          );
        }
        return mapShowPreparedItemToPrepDetails(response.data);
      },
      providesTags: (_result, _error, id) => [
        { type: "ProduceOutputs", id: `PreparedItem-${id}` },
      ],
      keepUnusedDataFor: 0,
    }),
    createProduceOutput: builder.mutation<void, CreateProduceOutputRequest>({
      query: (body) => ({
        url: Endpoints.produceOutput.create,
        method: "POST",
        body,
      }),
      transformResponse: (response: ApiResponse<unknown>): void => {
        if (!response.success) {
          throw new Error(response.message || "Failed to create produce output");
        }
      },
      invalidatesTags: [{ type: "ProduceOutputs", id: "LIST" }],
    }),
    getProducedOutputById: builder.query<
      ProduceOutputApiResponse,
      string | number
    >({
      query: (id) => Endpoints.produceOutput.showProduceOutputById(id),
      transformResponse: (
        response: ApiResponse<ProduceOutputApiResponse>,
      ): ProduceOutputApiResponse => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load produce output");
        }
        return response.data;
      },
      providesTags: (_result, _error, id) => [
        { type: "ProduceOutputs", id: String(id) },
      ],
      keepUnusedDataFor: 0,
    }),
    getShowPendingRecord: builder.query<
      ShowPendingRecordApiResponse,
      string | number
    >({
      query: (id) => Endpoints.produceOutput.showPendingRecord(id),
      transformResponse: (response: ApiResponse<ShowPendingRecordApiResponse>): ShowPendingRecordApiResponse => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load pending record output");
        }
        return response.data;
      },
      providesTags: (_result, _error, id) => [
        { type: "ProduceOutputs", id: `PendingRecord-${id}` },
      ],
    }),
    storeRecordOutput: builder.mutation<
      void,
      { id: string | number; data: FormData }
    >({
      query: ({ id, data }) => ({
        url: Endpoints.produceOutput.storeRecordOutput(id),
        method: "POST",
        body: data,
      }),
      transformResponse: (response: ApiResponse<unknown>): void => {
        if (!response.success) {
          throw new Error(response.message || "Failed to store record output");
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ProduceOutputs", id: "LIST" },
        { type: "ProduceOutputs", id: String(id) },
      ],
    }),
  }),
});

export const {
  useGetProduceOutputsQuery,
  useLazyGetProduceOutputsQuery,
  useGetProduceOutputHelperDataQuery,
  useGetShowPreparedItemQuery,
  useCreateProduceOutputMutation,
  useGetProducedOutputByIdQuery,
  useGetShowPendingRecordQuery,
  useStoreRecordOutputMutation,
} = producedOutputApi;
